<script>
import RoomCard from '../RoomCard.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';

export default {
  name: 'RoomTab',
  components: {
    RoomCard,
    LoadingState,
    ErrorState,
    EmptyState,
  },
  emits: ['go-general'],
  computed: {
    branchId() {
      return this.$route.params.branchId;
    },
    rooms() {
      return this.$store.getters['aoikumoRooms/getRooms'](this.branchId);
    },
    isLoading() {
      return this.$store.getters['aoikumoRooms/isLoading'];
    },
    error() {
      return this.$store.getters['aoikumoRooms/isError'];
    },
    // 400 means the branch is not linked yet (or the account has no Aoikumo
    // workspace). That is a setup step, not a failure.
    isNotLinked() {
      return this.error && this.error.status === 400;
    },
    // 429 / 502 / 504: Aoikumo is busy or down. These are real errors.
    isUnreachable() {
      return this.error && this.error.code === 'unreachable';
    },
    // The backend text is shown so a customer can quote it when reporting an
    // issue. The plain "not linked" text is already covered by the title.
    detailText() {
      const message = this.error && this.error.message;
      if (!message) return '';
      if (this.isNotLinked && /not linked/i.test(message)) return '';
      return message;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      return this.$store.dispatch('aoikumoRooms/fetchRooms', {
        branchId: this.branchId,
      });
    },
  },
};
</script>

<template>
  <div class="px-8 pb-4 overflow-y-auto">
    <LoadingState v-if="isLoading" message="Loading rooms..." />

    <EmptyState
      v-else-if="isNotLinked"
      data-testid="aoikumo-rooms-not-linked"
      :title="$t('AOIKUMO_ROOMS.NOT_LINKED_TITLE')"
      :message="$t('AOIKUMO_ROOMS.NOT_LINKED_HINT')"
    >
      <p
        v-if="detailText"
        class="mb-4 text-xs text-center text-slate-500 dark:text-slate-400"
        data-testid="aoikumo-rooms-detail"
      >
        {{ detailText }}
      </p>
      <div class="flex justify-center">
        <woot-button
          color-scheme="primary"
          data-testid="aoikumo-rooms-go-general"
          @click="$emit('go-general')"
        >
          {{ $t('AOIKUMO_ROOMS.GO_GENERAL') }}
        </woot-button>
      </div>
    </EmptyState>

    <div
      v-else-if="isUnreachable"
      data-testid="aoikumo-rooms-error"
      class="p-4 text-sm rounded-md bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200"
    >
      <p class="mb-2">
        {{ $t('INTEGRATION_SETTINGS.AOIKUMO.ALERTS.UNREACHABLE') }}
      </p>
      <p
        v-if="detailText"
        class="mb-2 text-xs opacity-80"
        data-testid="aoikumo-rooms-detail"
      >
        {{ detailText }}
      </p>
      <woot-button
        size="small"
        variant="smooth"
        data-testid="aoikumo-rooms-retry"
        @click="load"
      >
        {{ $t('AOIKUMO_ROOMS.RETRY') }}
      </woot-button>
    </div>

    <ErrorState v-else-if="error" />

    <EmptyState
      v-else-if="rooms.length === 0"
      title="No Room Found"
      message="No rooms are synced from Aoikumo for this branch yet."
    />

    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <RoomCard v-for="room in rooms" :key="room.id" :room="room" />
    </div>
  </div>
</template>
